import { AutoComplete, Empty, Input, Spin } from "antd";
import type { InputProps, InputRef } from "antd";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  formatSmartUidSuggestionLabel,
  parseSmartUidSearchResponse,
  searchUsersBySmartUidPrefix,
  type SmartUidSuggestionItem,
} from "../../../api/profile";
import "./smartUidAutocomplete.css";

const MIN_CHARS = 3;
const DEBOUNCE_MS = 400;

export type SmartUidAutoCompleteInputProps = Omit<
  InputProps,
  "value" | "onChange"
> & {
  value?: string;
  onChange?: (value: string) => void;
  /** Called when user picks a suggestion (after value is applied). */
  onSuggestionSelect?: (smartUID: string) => void;
};

export const SmartUidAutoCompleteInput = React.forwardRef<
  InputRef,
  SmartUidAutoCompleteInputProps
>(
  (
    {
      onBlur,
      onSuggestionSelect,
      placeholder,
      size,
      style,
      disabled,
      value,
      onChange,
      className,
      ...inputRest
    },
    ref
  ) => {
    const skipBlurAfterSelectRef = useRef(false);
    const [options, setOptions] = useState<
      { value: string; label: React.ReactNode }[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const trimmedValue = typeof value === "string" ? value.trim() : "";
    const shouldShowDropdown =
      trimmedValue.length >= MIN_CHARS && isDropdownVisible;

    const runFetch = useCallback(
      async (text: string) => {
        const q = text.trim();
        if (q.length < MIN_CHARS) {
          setIsSearching(false);
          setIsDropdownVisible(false);
          setLoading(false);
          setOptions([]);
          return;
        }
        setLoading(true);
        try {
          const res = await searchUsersBySmartUidPrefix(q);
          const items: SmartUidSuggestionItem[] =
            parseSmartUidSearchResponse(res);
          setOptions(
            items.map((item) => ({
              value: item.smartUID,
              label: (
                <span className="smart-uid-autocomplete-option-label">
                  {formatSmartUidSuggestionLabel(item)}
                </span>
              ),
            }))
          );
        } catch {
          setOptions([]);
        } finally {
          setIsSearching(false);
          setLoading(false);
        }
      },
      []
    );

    const debouncedFetch = useMemo(
      () => debounce(runFetch, DEBOUNCE_MS, { trailing: true }),
      [runFetch]
    );

    useEffect(() => {
      return () => debouncedFetch.cancel();
    }, [debouncedFetch]);

    const handleSearch = useCallback(
      (text: string) => {
        if (text.trim().length < MIN_CHARS) {
          debouncedFetch.cancel();
          setIsSearching(false);
          setIsDropdownVisible(false);
          setLoading(false);
          setOptions([]);
          return;
        }
        setIsSearching(true);
        setIsDropdownVisible(true);
        debouncedFetch(text);
      },
      [debouncedFetch]
    );

    /** AutoComplete root is not full-width by default (unlike Input), which breaks grid/Col layouts. */

    const rootStyle: React.CSSProperties = {
      width: "100%",
      maxWidth: "100%",
      display: "block",
    };

    const inputStyle: React.CSSProperties = {
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      ...style,
    };

    return (
      <div className={`smart-uid-autocomplete-root ${className}`}>
        <AutoComplete
          className="smart-uid-autocomplete"
          style={rootStyle}
          value={value}
          onChange={(v) => onChange?.(String(v))}
          options={options}
          onSearch={handleSearch}
          filterOption={false}
          disabled={disabled}
          open={shouldShowDropdown}
          notFoundContent={
            isSearching || loading ? (
              <div className="smart-uid-autocomplete-loading">
                <Spin size="small" />
              </div>
            ) : (
              <Empty
                className="smart-uid-autocomplete-empty"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No data"
              />
            )
          }
          onSelect={(selected) => {
            skipBlurAfterSelectRef.current = true;
            setIsDropdownVisible(false);
            const uid = String(selected).trim();
            onChange?.(uid);
            onSuggestionSelect?.(uid);
            window.setTimeout(() => {
              skipBlurAfterSelectRef.current = false;
            }, 0);
          }}
          dropdownMatchSelectWidth
          popupClassName="smart-uid-autocomplete-dropdown"
          dropdownStyle={{ padding: 0 }}
        >
          <Input
            ref={ref}
            {...inputRest}
            placeholder={placeholder}
            size={size}
            style={inputStyle}
            disabled={disabled}
            onBlur={(e) => {
              setIsDropdownVisible(false);
              if (skipBlurAfterSelectRef.current) return;
              onBlur?.(e);
            }}
          />
        </AutoComplete>
      </div>
    );
  }
);

SmartUidAutoCompleteInput.displayName = "SmartUidAutoCompleteInput";
